import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { RingingAlarm } from '@/hooks/useTaskAlarms';

interface Props {
  alarm: RingingAlarm | null;
  onDismiss: () => void;
  onOpenTask?: (instanceId: string) => void;
}

export function TaskAlarmOverlay({ alarm, onDismiss, onOpenTask }: Props) {
  const { t } = useTranslation();
  if (!alarm) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="mx-auto w-14 h-14 rounded-full bg-warning/15 flex items-center justify-center mb-2">
            <BellRing className="w-7 h-7 text-warning animate-pulse" />
          </div>
          <DialogTitle className="text-center">{t('taskAlarm.title')}</DialogTitle>
          <DialogDescription className="text-center">
            <span className="block font-medium text-foreground">{alarm.name}</span>
            {t('taskAlarm.scheduledFor', { time: format(parseISO(alarm.scheduledFor), 'HH:mm') })}
            {alarm.repeatIndex > 0 && ` • ${t('taskAlarm.reminder', { count: alarm.repeatIndex })}`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {onOpenTask && (
            <Button className="w-full" onClick={() => onOpenTask(alarm.instanceId)}>
              {t('taskAlarm.register')}
            </Button>
          )}
          <Button variant="outline" className="w-full" onClick={onDismiss}>
            {t('taskAlarm.dismiss')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
